import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  Grid,
  Container,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '0 solid #e2e8f0',
  borderRadius: '12px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  height: '100%',
}));

const MetricCardSkeleton = () => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 3, sm: 2 } }}>
      {/* Icon and Title Row */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 2,
        mb: 2
      }}>
        <Skeleton 
          variant="rounded" 
          width={40} 
          height={40} 
          sx={{ 
            borderRadius: 2,
            bgcolor: '#f8fafc'
          }} 
        />
        <Skeleton 
          variant="text" 
          width="60%" 
          height={24} 
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }} 
        />
      </Box>
      
      {/* Value */}
      <Skeleton 
        variant="text" 
        width="80%" 
        height={48} 
        sx={{ 
          mb: 1,
          fontSize: { xs: '1.5rem', sm: '1.875rem' }
        }} 
      />

      {/* Subtitle */}
      <Skeleton 
        variant="text" 
        width="90%" 
        height={20} 
        sx={{ 
          fontSize: { xs: '0.75rem', sm: '0.875rem' }
        }} 
      />
    </CardContent>
  </StyledCard>
);

const ChartSkeleton = ({ height = { xs: 300, sm: 350, md: 400 } }) => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Chart Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Skeleton 
          variant="text" 
          width={200} 
          height={32} 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }} 
        />
      </Box>
      
      {/* Chart Area */}
      <Skeleton 
        variant="rectangular" 
        height={height.xs || height} 
        sx={{ 
          borderRadius: 2,
          bgcolor: '#f8fafc'
        }} 
      />
    </CardContent>
  </StyledCard>
);

const SidebarCardSkeleton = () => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Skeleton 
        variant="text" 
        width={200} 
        height={32} 
        sx={{ 
          mb: 3,
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }} 
      />
      
      {/* Search Bar */}
      <Skeleton 
        variant="rounded" 
        width="100%" 
        height={40} 
        sx={{ 
          mb: 3,
          borderRadius: 2,
          bgcolor: '#f8fafc'
        }} 
      />
      
      {/* List Items */}
      {[1, 2, 3, 4, 5].map((item) => (
        <Box 
          key={item}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 2,
            p: 2,
            bgcolor: '#f8fafc',
            borderRadius: 2
          }}
        >
          <Skeleton 
            variant="circular" 
            width={40} 
            height={40} 
            sx={{ bgcolor: '#e2e8f0' }}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton 
              variant="text" 
              width="70%" 
              height={20} 
              sx={{ mb: 1 }}
            />
            <Skeleton 
              variant="text" 
              width="50%" 
              height={16} 
            />
          </Box>
          <Skeleton 
            variant="rounded" 
            width={60} 
            height={24} 
            sx={{ borderRadius: 2 }}
          />
        </Box>
      ))}
    </CardContent>
  </StyledCard>
);

const WorkTypeDistributionSkeleton = () => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Skeleton 
        variant="text" 
        width={200} 
        height={32} 
        sx={{ 
          mb: 3,
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }} 
      />
      
      {/* Distribution Items */}
      {[1, 2, 3, 4].map((item) => (
        <Box 
          key={item}
          sx={{ 
            mb: 2,
            p: 2,
            bgcolor: '#f8fafc',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Skeleton 
              variant="text" 
              width="40%" 
              height={20} 
            />
            <Skeleton 
              variant="text" 
              width="20%" 
              height={20} 
            />
          </Box>
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={8} 
            sx={{ borderRadius: 1 }}
          />
        </Box>
      ))}
    </CardContent>
  </StyledCard>
);

const TableSkeleton = () => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 3, sm: 4 },
      borderRadius: 1,
      border: '0 solid #e2e8f0',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      bgcolor: '#ffffff'
    }}
  >
    {/* Table Header */}
    <Skeleton 
      variant="text" 
      width={200} 
      height={32} 
      sx={{ 
        mb: 3,
        fontSize: { xs: '1rem', sm: '1.25rem' }
      }} 
    />
    
    {/* Search Bar */}
    <Skeleton 
      variant="rounded" 
      width="100%" 
      height={40} 
      sx={{ 
        mb: 3,
        borderRadius: 2,
        bgcolor: '#f8fafc'
      }} 
    />
    
    {/* Table Headers */}
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Skeleton 
          key={item}
          variant="text" 
          width="100%" 
          height={24} 
        />
      ))}
    </Box>
    
    {/* Table Rows */}
    {[1, 2, 3, 4, 5].map((row) => (
      <Box 
        key={row}
        sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 2,
          p: 2,
          bgcolor: row % 2 === 0 ? '#fafafa' : '#ffffff',
          borderRadius: 1
        }}
      >
        {[1, 2, 3, 4, 5, 6].map((cell) => (
          <Skeleton 
            key={cell}
            variant="text" 
            width="100%" 
            height={20} 
          />
        ))}
      </Box>
    ))}
  </Paper>
);

const FilterSkeleton = () => (
  <Box sx={{ 
    borderRadius: 1,
    p: { xs: 2, sm: 4 },
    border: '1px solid #e2e8f0',
    bgcolor: '#ffffff',
    mb: { xs: 3, md: 4 }
  }}>
    {/* Filter Header */}
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2, 
      flexWrap: 'wrap',
      mb: 2
    }}>
      <Skeleton 
        variant="circular" 
        width={20} 
        height={20} 
        sx={{ bgcolor: '#e2e8f0' }}
      />
      <Skeleton 
        variant="text" 
        width={100} 
        height={24} 
      />
      <Skeleton 
        variant="rounded" 
        width={140} 
        height={40} 
        sx={{ borderRadius: 1 }}
      />
      <Skeleton 
        variant="rounded" 
        width={140} 
        height={40} 
        sx={{ borderRadius: 1 }}
      />
      <Skeleton 
        variant="rounded" 
        width={140} 
        height={40} 
        sx={{ borderRadius: 1 }}
      />
    </Box>
  </Box>
);

const PRAnalysisSkeleton = () => (
  <Box sx={{ 
    bgcolor: '#fbfbfb', 
    minHeight: '100vh', 
    p: 0
  }}>
    <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" }, p: '0px !important' }}>
      {/* Filters Skeleton */}
      <FilterSkeleton />

      {/* Stats Cards Skeleton */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} lg={3} key={item}>
            <MetricCardSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* Chart and Open PRs Section */}
      <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mb: { xs: 4, md: 5 } }}>
        <Grid item xs={12} lg={8}>
          <ChartSkeleton height={{ xs: 300, sm: 350, md: 400 }} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <SidebarCardSkeleton />
        </Grid>
      </Grid>

      {/* PR Details Table Skeleton */}
      <TableSkeleton />
    </Container>
  </Box>
);

const JiraAnalysisSkeleton = () => (
  <Box sx={{ 
    bgcolor: '#fbfbfb', 
    minHeight: '100vh', 
    p: 0
  }}>
    <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" }, p: '0px !important' }}>
      {/* Filters Skeleton */}
      <FilterSkeleton />

      {/* Stats Cards Skeleton */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} lg={3} key={item}>
            <MetricCardSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* Chart and Work Type Distribution Section */}
      <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mb: { xs: 3, md: 4 } }}>
        <Grid item xs={12} lg={8}>
          <ChartSkeleton height={{ xs: 300, sm: 350, md: 400 }} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <WorkTypeDistributionSkeleton />
        </Grid>
      </Grid>

      {/* Table Filters Skeleton */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 1,
          border: '0 solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          mb: { xs: 3, md: 4 },
          bgcolor: '#ffffff'
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton 
              variant="rounded" 
              height={40} 
              sx={{ borderRadius: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton 
              variant="rounded" 
              height={40} 
              sx={{ borderRadius: 1 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* JIRA Details Table Skeleton */}
      <TableSkeleton />
    </Container>
  </Box>
);

export {
  MetricCardSkeleton,
  ChartSkeleton,
  SidebarCardSkeleton,
  WorkTypeDistributionSkeleton,
  TableSkeleton,
  FilterSkeleton,
  PRAnalysisSkeleton,
  JiraAnalysisSkeleton,
};

